import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Star, Lightbulb, Shield, Globe, ArrowRight } from 'lucide-react';

export const AboutPage = () => {
    const values = [
        {
            icon: Star,
            title: 'Excellence',
            description: 'We strive for excellence in everything we do, delivering exceptional quality in our services and products.',
        },
        {
            icon: Lightbulb,
            title: 'Innovation',
            description: 'We embrace innovative technologies and approaches to solve complex business challenges.',
        },
        {
            icon: Shield,
            title: 'Reliability',
            description: 'Our clients trust us to deliver on time, every time, with consistent quality and professionalism.',
        },
        {
            icon: Globe,
            title: 'Global Reach',
            description: 'We connect businesses worldwide, bridging markets and creating opportunities across borders.',
        },
    ];

    return (
        <main className="pt-20" data-testid="about-page">
            {/* Hero Section */}
            <section className="py-16 lg:py-24 bg-white" data-testid="about-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                            About Us
                        </p>
                        <h1 className="text-4xl sm:text-5xl font-black text-[#3C3B6E] tracking-tight leading-tight mb-6">
                            Building Global Connections Through Technology
                        </h1>
                        <p className="text-lg text-gray-600 leading-relaxed mb-6" data-testid="company-overview">
                            BIONEXX LLC is a Kentucky-based LLC specializing in IT software development, app development, design services, and professional export and import solutions. We are committed to delivering reliable, high-quality services to clients worldwide.
                        </p>
                        <Link to="/contact">
                            <Button 
                                className="bg-[#B22234] hover:bg-[#8B1A1A] text-white rounded-md px-6 font-semibold"
                                data-testid="about-cta-button"
                            >
                                Get in Touch
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-16 lg:py-24 bg-gray-50" data-testid="mission-vision">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16">
                        {/* Mission */}
                        <div className="bg-white p-8 lg:p-12 rounded-lg border border-gray-200">
                            <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                                Our Mission
                            </p>
                            <h2 className="text-2xl sm:text-3xl font-black text-[#3C3B6E] tracking-tight mb-4">
                                What Drives Us
                            </h2>
                            <p className="text-gray-600 leading-relaxed" data-testid="mission-statement">
                                To provide innovative IT solutions and high-quality products, connecting businesses globally with trust and professionalism.
                            </p>
                        </div>

                        {/* Vision */}
                        <div className="bg-[#3C3B6E] p-8 lg:p-12 rounded-lg">
                            <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                                Our Vision
                            </p>
                            <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-4">
                                Where We&apos;re Headed
                            </h2>
                            <p className="text-white/80 leading-relaxed" data-testid="vision-statement">
                                To be a leading global IT and export company, recognized for quality, reliability, and innovation.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Values */}
            <section className="py-16 lg:py-24 bg-white" data-testid="values-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-left mb-12 lg:mb-16">
                        <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                            Our Core Values
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-black text-[#3C3B6E] tracking-tight">
                            What We Stand For
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map((value, index) => (
                            <div 
                                key={index}
                                className="p-6 bg-gray-50 border border-gray-200 rounded-lg transition-all hover:border-[#B22234] hover:-translate-y-1"
                                data-testid={`value-card-${index}`}
                            >
                                <div className="w-12 h-12 flex items-center justify-center bg-[#3C3B6E] rounded-lg mb-4">
                                    <value.icon className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-lg font-bold text-[#3C3B6E] mb-2 tracking-tight">
                                    {value.title}
                                </h3>
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    {value.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-16 lg:py-24 bg-[#3C3B6E]" data-testid="team-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                        Our Team
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-6">
                        Expert Professionals at Your Service
                    </h2>
                    <p className="text-white/80 leading-relaxed max-w-2xl mx-auto mb-8">
                        Our team consists of experienced professionals in software development, IT consulting, design, and international trade. We bring together diverse expertise to deliver comprehensive solutions for your business needs.
                    </p>
                    <Link to="/services">
                        <Button 
                            variant="outline"
                            className="border-2 border-white text-white hover:bg-white hover:text-[#3C3B6E] rounded-md px-6"
                            data-testid="view-services-button"
                        >
                            View Our Services
                            <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </Link>
                </div>
            </section>
        </main>
    );
};
